import axios from "axios";

import keys from "../config/keys";

import { removeRequest } from "./request.actions";

import {
  SET_VIDEO_FILES,
  SET_OFFICER_NAME,
  SET_VIDEO_REDACTION_PURPOSE,
  APPEND_VIDEO_FILES,
  SET_RAW_VIDEOS,
  SET_REDACTED_VIDEOS,
  RESET_FORM,
  SET_UPLOADING,
  SET_FILES_UPLOADING_PROGRESS,
  SET_AUDIO_FILES,
  APPEND_AUDIO_FILES,
} from "./types";

const { SERVER_URL } = keys;

export const setOfficerName = (name) => ({
  type: SET_OFFICER_NAME,
  payload: name,
});

export const setVideoRedactionPurpose = (purpose) => ({
  type: SET_VIDEO_REDACTION_PURPOSE,
  payload: purpose,
});

export const setVideoFiles = (files) => ({
  type: SET_VIDEO_FILES,
  payload: files,
});

export const appendVideoFiles = (files) => ({
  type: APPEND_VIDEO_FILES,
  payload: files,
});

export const setAudioFiles = (files) => ({
  type: SET_AUDIO_FILES,
  payload: files,
});

export const appendAudioFiles = (files) => ({
  type: APPEND_AUDIO_FILES,
  payload: files,
});

export const setRawVideos = (videos) => ({
  type: SET_RAW_VIDEOS,
  payload: videos,
});

export const setRedactedVideos = (videos) => ({
  type: SET_REDACTED_VIDEOS,
  payload: videos,
});

export const resetForm = () => ({
  type: RESET_FORM,
});

export const setUploading = (status) => ({
  type: SET_UPLOADING,
  payload: status,
});

export const setUploadingProgress = (progress) => ({
  type: SET_FILES_UPLOADING_PROGRESS,
  payload: progress,
});

export const uploadVideos = (formData) => async (dispatch) => {
  try {
    const config = {
      requestId: "uploadVideos",
      onUploadProgress: (ProgressEvent) => {
        let uploadedProgress =
          (ProgressEvent.loaded / ProgressEvent.total) * 100;
        uploadedProgress = (uploadedProgress * 0.5).toFixed(0); // Taking only 50% for upload progress

        return dispatch({
          type: SET_FILES_UPLOADING_PROGRESS,
          payload: parseInt(uploadedProgress, 10),
        });
      },
    };
    const res = await axios.post(
      `${SERVER_URL}/redact/videos`,
      formData,
      config
    );
    return res.data;
  } catch (e) {
    dispatch(removeRequest("uploadVideos"));
    if (axios.isCancel(e)) {
      return true;
    } else {
      console.log(e);
      return false;
    }
  }
};

// Get SAS URL for direct upload to Azure (for files >5GB)
export const getDirectUploadUrl = (officerName, purpose, fileName) => async () => {
  try {
    const res = await axios.post(
      `${SERVER_URL}/redact/videos/direct-upload`,
      { officerName, purpose, fileName },
      { requestId: "getDirectUploadUrl" }
    );
    return res.data;
  } catch (e) {
    if (axios.isCancel(e)) {
      return { success: true };
    } else {
      console.log(e);
      return { success: false };
    }
  }
};

// Upload file directly to Azure using SAS URL
export const uploadToAzureDirectly = (file, uploadUrl, onProgress) => async (dispatch) => {
  try {
    const { BlockBlobClient } = await import("@azure/storage-blob");
    const blockBlobClient = new BlockBlobClient(uploadUrl);
    
    await blockBlobClient.uploadData(file, {
      blockSize: 4 * 1024 * 1024, // 4MB blocks
      concurrency: 5,
      onProgress: (ev) => {
        if (onProgress) {
          const percent = Math.round((ev.loadedBytes / file.size) * 100);
          onProgress(percent);
          dispatch({
            type: SET_FILES_UPLOADING_PROGRESS,
            payload: percent,
          });
        }
      },
    });
    
    return { success: true };
  } catch (e) {
    console.log("Azure upload error:", e);
    return { success: false, error: e.message };
  }
};

// Generate a thumbnail from a video file using the Canvas API (browser-supported formats only)
export const generateThumbnail = (file) =>
  new Promise((resolve) => {
    const video = document.createElement("video");
    const url = URL.createObjectURL(file);
    const cleanup = () => URL.revokeObjectURL(url);

    const timeout = setTimeout(() => { cleanup(); resolve(null); }, 8000);

    const drawFrame = () => {
      clearTimeout(timeout);
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext("2d").drawImage(video, 0, 0);
      cleanup();
      canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.8);
    };

    video.preload = "metadata";
    video.muted = true;
    video.src = url;
    video.onloadedmetadata = () => { video.currentTime = 1; };
    video.onseeked = drawFrame;
    video.onerror = () => { clearTimeout(timeout); cleanup(); resolve(null); };
  });

// Upload a thumbnail blob directly to Azure using a SAS URL
export const uploadThumbnailToAzure = (thumbBlob, thumbUploadUrl) => async () => {
  try {
    const { BlockBlobClient } = await import("@azure/storage-blob");
    const client = new BlockBlobClient(thumbUploadUrl);
    await client.uploadData(thumbBlob, {
      blobHTTPHeaders: { blobContentType: "image/jpeg" },
    });
    return true;
  } catch {
    return false;
  }
};

// Complete direct upload after file is uploaded to Azure
export const completeDirectUpload = (redactionID, videoID, blobName, thumbBlobName) => async () => {
  try {
    const res = await axios.post(
      `${SERVER_URL}/redact/videos/direct-upload/complete`,
      { redactionID, videoID, blobName, thumbBlobName },
      { requestId: "completeDirectUpload" }
    );
    return res.data;
  } catch (e) {
    if (axios.isCancel(e)) {
      return { success: true };
    } else {
      console.log(e);
      return { success: false };
    }
  }
};

export const getUploadProgress = (redactionID) => async () => {
  try {
    const res = await axios.get(
      `${SERVER_URL}/redact/${redactionID}/upload/progress?buster=` +
        new Date().getTime(),
      { requestId: "getUploadProgress" }
    );
    if (res.data.success) {
      const { status, progress } = res.data;
      if (status === "Failed") {
        return false;
      }
      return { status, realProgress: progress };
    } else {
      return false;
    }
  } catch (e) {
    if (axios.isCancel(e)) {
      return { success: true };
    } else {
      console.log(e);
      return false;
    }
  }
};

export const fetchVideos = () => async (dispatch) => {
  try {
    const res = await axios.get(
      `${SERVER_URL}/redact/videos?buster=` + new Date().getTime(),
      {
        requestId: "fetchVideos",
      }
    );
    if (res.data.success) {
      if (res.data.rawVideos.length === 0) {
        dispatch({
          type: SET_RAW_VIDEOS,
          payload: null,
        });
      } else {
        dispatch({
          type: SET_RAW_VIDEOS,
          payload: res.data.rawVideos,
        });
      }
      return true;
    } else {
      dispatch({
        type: SET_RAW_VIDEOS,
        payload: null,
      });
      return false;
    }
  } catch (e) {
    dispatch({
      type: SET_RAW_VIDEOS,
      payload: null,
    });
    if (axios.isCancel(e)) {
      return true;
    } else {
      console.log(e);
      return false;
    }
  }
};

export const fetchRedactedVideos = () => async (dispatch) => {
  try {
    const res = await axios.get(`${SERVER_URL}/redact/videos/redacted`, {
      requestId: "fetchRedactedVideos",
    });
    if (res.data.success) {
      if (res.data.redactedVideos.length === 0) {
        dispatch({
          type: SET_REDACTED_VIDEOS,
          payload: null,
        });
      } else {
        dispatch({
          type: SET_REDACTED_VIDEOS,
          payload: res.data.redactedVideos,
        });
      }
      return true;
    } else {
      dispatch({
        type: SET_REDACTED_VIDEOS,
        payload: null,
      });
      return false;
    }
  } catch (e) {
    if (axios.isCancel(e)) {
      return true;
    } else {
      console.log(e);
      dispatch({
        type: SET_REDACTED_VIDEOS,
        payload: null,
      });
      return false;
    }
  }
};

export const deleteVideo = (videoID) => async () => {
  try {
    const res = await axios.delete(`${SERVER_URL}/redact/videos/${videoID}`, {
      requestId: "deleteVideo",
    });
    return res.data;
  } catch (e) {
    if (axios.isCancel(e)) {
      return true;
    } else {
      console.log(e);
      return false;
    }
  }
};

export const getIframeURL = (videoID) => async () => {
  try {
    const res = await axios.get(
      `${SERVER_URL}/redact/videos/${videoID}/redact`
    );
    return {
      iframeSrc: decodeURIComponent(res.data.iframeSrc),
      notes: res.data.notes,
      name: res.data.name,
    };
  } catch (e) {
    if (axios.isCancel(e)) {
      return true;
    } else {
      console.log(e);
      return false;
    }
  }
};

export const saveMetadata = (videoID, metadata, notes) => async () => {
  try {
    const res = await axios.post(
      `${SERVER_URL}/redact/videos/${videoID}/save`,
      { metadata, notes },
      { requestId: "saveMetadata" }
    );
    return res.data;
  } catch (e) {
    if (axios.isCancel(e)) {
      return true;
    } else {
      console.log(e);
      return false;
    }
  }
};

export const saveRedactedVideo = (videoID) => async () => {
  try {
    const res = await axios.get(
      `${SERVER_URL}/redact/videos/${videoID}/redacted/save`,
      { requestId: "saveRedactedVideo" }
    );
    return res.data;
  } catch (e) {
    if (axios.isCancel(e)) {
      return true;
    } else {
      console.log(e);
      return false;
    }
  }
};

export const deleteRedactedVideo = (videoID) => async () => {
  try {
    const res = await axios.delete(
      `${SERVER_URL}/redact/videos/${videoID}/redacted?buster=` +
        new Date().getTime(),
      { requestId: "deleteRedactedVideo" }
    );
    return res.data;
  } catch (e) {
    if (axios.isCancel(e)) {
      return true;
    } else {
      console.log(e);
      return false;
    }
  }
};
